/**
 * InstitutionReportsPage.jsx — FusionLead institution reports.
 * Shaped by: G2a; owner 2026-10-12.
 * Invariants: access is RequireLauncherApp appId="fusionlead". Institution
 *   scoping stays on the caller's institution id.
 * Related: appCatalog.js FLAG_APP_IDS, ReadinessDashboard.jsx.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import DashboardPageHeader from "components/common/DashboardPageHeader";
import DataLoadErrorAlert from "components/common/DataLoadErrorAlert";
import { useAuth } from "hooks/useAuth";
import RequireLauncherApp from "components/appLauncher/RequireLauncherApp";
import { listTciRegistry } from "services/fde/fdeRegistryService";
import {
  createSavedReport,
  deleteSavedReport,
  listSavedReportsForUser,
  shareSavedReport,
} from "services/fde/savedReportsService";
import {
  createReportSchedule,
  deleteReportSchedule,
  listReportSchedulesForUser,
} from "services/fde/reportSchedulesService";
import { exportInstitutionReportPdf } from "services/fde/reportPdfService";
import {
  METRIC_LABELS,
  downloadReportsCsv,
  listInstitutionReportsInRange,
} from "services/fde/reportingService";
import {
  ALL_METRIC_KEYS,
  buildReportChartData,
  buildReportTableRows,
  defaultPeriodRange,
  parsePeriodKey,
  toPeriodKey,
} from "services/fde/reportingTransforms";
import ActivityReportChart from "./ActivityReportChart";

const inlineLoading = (
  <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
    <CircularProgress />
  </Box>
);

const CADENCE_OPTIONS = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
];

const InstitutionReportsContent = () => {
  const { user, userData, organizationId } = useAuth();
  const institutionId =
    userData?.activeInstitutionId || userData?.institutionId || organizationId || null;

  const defaultRange = useMemo(() => defaultPeriodRange(), []);

  const [reports, setReports] = useState([]);
  const [tciNameById, setTciNameById] = useState({});
  const [savedViews, setSavedViews] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [exportingPdf, setExportingPdf] = useState(false);

  const [startPeriodKey, setStartPeriodKey] = useState(defaultRange.startPeriodKey);
  const [endPeriodKey, setEndPeriodKey] = useState(defaultRange.endPeriodKey);
  const [dimension, setDimension] = useState("institution");
  const [selectedMetrics, setSelectedMetrics] = useState([...ALL_METRIC_KEYS]);

  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveViewName, setSaveViewName] = useState("");
  const [savingView, setSavingView] = useState(false);

  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareTargetUid, setShareTargetUid] = useState("");
  const [sharingViewId, setSharingViewId] = useState("");
  const [sharingView, setSharingView] = useState(false);

  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [scheduleViewId, setScheduleViewId] = useState("");
  const [scheduleCadence, setScheduleCadence] = useState("monthly");
  const [schedulingView, setSchedulingView] = useState(false);

  const profileReady = userData !== undefined;

  const currentFilters = useMemo(
    () => ({
      startPeriodKey,
      endPeriodKey,
      dimension,
      metrics: selectedMetrics,
    }),
    [startPeriodKey, endPeriodKey, dimension, selectedMetrics],
  );

  const reloadSavedData = async () => {
    if (!user?.uid) return;
    const [views, scheduleRows] = await Promise.all([
      listSavedReportsForUser(user.uid),
      listReportSchedulesForUser(user.uid),
    ]);
    setSavedViews(views);
    setSchedules(scheduleRows);
  };

  useEffect(() => {
    if (!profileReady) return undefined;

    if (!institutionId) {
      setReports([]);
      setTciNameById({});
      setLoading(false);
      setLoadError(false);
      setErrorMessage("");
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setLoadError(false);
    setErrorMessage("");

    Promise.all([
      listInstitutionReportsInRange(institutionId, startPeriodKey, endPeriodKey),
      listTciRegistry({ institutionId }),
    ])
      .then(([reportRows, tciRegistry]) => {
        if (cancelled) return;
        setReports(reportRows);
        setTciNameById(
          Object.fromEntries((tciRegistry || []).map((row) => [row.id, row.name || row.id])),
        );
      })
      .catch((err) => {
        if (!cancelled) {
          setLoadError(true);
          setErrorMessage(err?.message || "Failed to load reports.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [profileReady, institutionId, startPeriodKey, endPeriodKey]);

  useEffect(() => {
    if (!profileReady || !user?.uid) return undefined;
    reloadSavedData().catch((err) => {
      setErrorMessage(err?.message || "Failed to load saved views.");
    });
    return undefined;
  }, [profileReady, user?.uid]);

  const tableRows = useMemo(
    () =>
      buildReportTableRows(reports, {
        dimension,
        metrics: selectedMetrics,
        tciNameById,
      }),
    [reports, dimension, selectedMetrics, tciNameById],
  );

  const chartData = useMemo(
    () =>
      buildReportChartData(reports, {
        dimension,
        metrics: selectedMetrics,
      }),
    [reports, dimension, selectedMetrics],
  );

  const metricKeys = selectedMetrics.filter((key) => METRIC_LABELS[key]);

  const applySavedView = (view) => {
    const filters = view.filters || {};
    if (filters.startPeriodKey) setStartPeriodKey(filters.startPeriodKey);
    if (filters.endPeriodKey) setEndPeriodKey(filters.endPeriodKey);
    if (filters.dimension) setDimension(filters.dimension);
    if (Array.isArray(filters.metrics) && filters.metrics.length) {
      setSelectedMetrics(filters.metrics);
    }
  };

  const handleMetricToggle = (metricKey) => {
    setSelectedMetrics((current) => {
      if (current.includes(metricKey)) {
        const next = current.filter((key) => key !== metricKey);
        return next.length ? next : current;
      }
      return [...current, metricKey];
    });
  };

  const handleSaveView = async () => {
    if (!user?.uid || !institutionId || !saveViewName.trim()) return;
    setSavingView(true);
    setErrorMessage("");
    try {
      await createSavedReport({
        uid: user.uid,
        institutionId,
        name: saveViewName.trim(),
        filters: currentFilters,
      });
      setSaveDialogOpen(false);
      setSaveViewName("");
      setSuccessMessage("View saved.");
      await reloadSavedData();
    } catch (err) {
      setErrorMessage(err?.message || "Failed to save view.");
    } finally {
      setSavingView(false);
    }
  };

  const handleShareView = async () => {
    if (!sharingViewId || !shareTargetUid.trim()) return;
    setSharingView(true);
    setErrorMessage("");
    try {
      await shareSavedReport(sharingViewId, shareTargetUid.trim(), institutionId);
      setShareDialogOpen(false);
      setShareTargetUid("");
      setSharingViewId("");
      setSuccessMessage("View shared.");
      await reloadSavedData();
    } catch (err) {
      setErrorMessage(err?.message || "Failed to share view.");
    } finally {
      setSharingView(false);
    }
  };

  const handleScheduleView = async () => {
    if (!user?.uid || !institutionId || !scheduleViewId) return;
    setSchedulingView(true);
    setErrorMessage("");
    try {
      const accountEmail = user.email || "";
      await createReportSchedule({
        uid: user.uid,
        institutionId,
        savedReportId: scheduleViewId,
        cadence: scheduleCadence,
        // Delivery is locked to the creator's verified Auth email (not free-text).
        recipientEmail: accountEmail,
        accountEmail,
        emailVerified: Boolean(user.emailVerified),
      });
      setScheduleDialogOpen(false);
      setScheduleViewId("");
      setSuccessMessage("Delivery schedule created.");
      await reloadSavedData();
    } catch (err) {
      setErrorMessage(err?.message || "Failed to create schedule.");
    } finally {
      setSchedulingView(false);
    }
  };

  const handleExportPdf = async () => {
    if (!institutionId) return;
    setExportingPdf(true);
    setErrorMessage("");
    try {
      await exportInstitutionReportPdf({
        institutionId,
        ...currentFilters,
      });
    } catch (err) {
      setErrorMessage(err?.message || "Failed to export PDF.");
    } finally {
      setExportingPdf(false);
    }
  };

  if (!profileReady) return inlineLoading;

  if (!institutionId) {
    return (
      <Stack spacing={3}>
        <DashboardPageHeader
          title="Institution reports"
          subtitle="Monthly ASI loop metrics aggregated for leadership reporting."
        />
        <Alert severity="info">
          No institution is linked to your account yet. Complete institution setup or ask an
          administrator to assign your institution.
        </Alert>
      </Stack>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Stack spacing={3}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
          <Box sx={{ flex: 1 }}>
            <DashboardPageHeader
              title="Institution reports"
              subtitle="Build activity reports from pre-aggregated loop metrics."
            />
          </Box>
          <Stack direction="row" spacing={1}>
            <LoadingButton variant="outlined" onClick={() => setSaveDialogOpen(true)}>
              Save this view
            </LoadingButton>
            <LoadingButton
              variant="outlined"
              disabled={!reports.length}
              onClick={() =>
                downloadReportsCsv(
                  reports,
                  tciNameById,
                  `institution-reports-${institutionId}.csv`,
                  { dimension, metrics: selectedMetrics },
                )
              }
            >
              Export CSV
            </LoadingButton>
            <LoadingButton
              variant="outlined"
              loading={exportingPdf}
              disabled={!reports.length}
              onClick={handleExportPdf}
            >
              Export PDF
            </LoadingButton>
          </Stack>
        </Box>

        {successMessage ? (
          <Alert severity="success" onClose={() => setSuccessMessage("")}>
            {successMessage}
          </Alert>
        ) : null}

        {loadError ? (
          <DataLoadErrorAlert message={errorMessage || "Failed to load reports."} />
        ) : errorMessage ? (
          <Alert severity="error" onClose={() => setErrorMessage("")}>
            {errorMessage}
          </Alert>
        ) : null}

        <Paper sx={{ p: 2 }}>
          <Stack spacing={2}>
            <Typography variant="h6">Report builder</Typography>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <DatePicker
                label="Start month"
                views={["year", "month"]}
                value={parsePeriodKey(startPeriodKey)}
                onChange={(date) => {
                  const next = toPeriodKey(date);
                  if (next) setStartPeriodKey(next);
                }}
                slotProps={{ textField: { fullWidth: true } }}
              />
              <DatePicker
                label="End month"
                views={["year", "month"]}
                value={parsePeriodKey(endPeriodKey)}
                onChange={(date) => {
                  const next = toPeriodKey(date);
                  if (next) setEndPeriodKey(next);
                }}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Stack>

            <FormControl component="fieldset">
              <FormLabel component="legend">Dimension</FormLabel>
              <ToggleButtonGroup
                exclusive
                value={dimension}
                onChange={(_, value) => value && setDimension(value)}
                size="small"
              >
                <ToggleButton value="institution">Institution totals</ToggleButton>
                <ToggleButton value="tci">Per-TCI breakdown</ToggleButton>
              </ToggleButtonGroup>
            </FormControl>

            <FormControl component="fieldset">
              <FormLabel component="legend">Metrics</FormLabel>
              <FormGroup row>
                {ALL_METRIC_KEYS.map((metricKey) => (
                  <FormControlLabel
                    key={metricKey}
                    control={
                      <Checkbox
                        checked={selectedMetrics.includes(metricKey)}
                        onChange={() => handleMetricToggle(metricKey)}
                      />
                    }
                    label={METRIC_LABELS[metricKey]}
                  />
                ))}
              </FormGroup>
            </FormControl>
          </Stack>
        </Paper>

        <Box
          sx={{
            display: "grid",
            gap: 3,
            gridTemplateColumns: { xs: "1fr", lg: "280px 1fr" },
          }}
        >
          <Paper sx={{ p: 1 }}>
            <Typography variant="subtitle1" sx={{ px: 1, py: 1 }}>
              Saved views
            </Typography>
            {!savedViews.length ? (
              <Typography color="text.secondary" variant="body2" sx={{ px: 1, pb: 1 }}>
                No saved views yet.
              </Typography>
            ) : (
              <List dense>
                {savedViews.map((view) => (
                  <ListItem
                    key={view.id}
                    disablePadding
                    secondaryAction={
                      view.uid === user?.uid ? (
                        <Stack direction="row">
                          <IconButton
                            edge="end"
                            aria-label="Schedule view"
                            onClick={() => {
                              setScheduleViewId(view.id);
                              setScheduleDialogOpen(true);
                            }}
                          >
                            <ScheduleOutlinedIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            edge="end"
                            aria-label="Share view"
                            onClick={() => {
                              setSharingViewId(view.id);
                              setShareDialogOpen(true);
                            }}
                          >
                            <ShareOutlinedIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            edge="end"
                            aria-label="Delete view"
                            onClick={async () => {
                              await deleteSavedReport(view.id);
                              await reloadSavedData();
                            }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      ) : null
                    }
                  >
                    <ListItemButton onClick={() => applySavedView(view)}>
                      <ListItemText
                        primary={view.name}
                        secondary={
                          view.uid === user?.uid ? "Owned by you" : "Shared with you"
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}

            <Typography variant="subtitle1" sx={{ px: 1, py: 1, mt: 1 }}>
              Schedules
            </Typography>
            {!schedules.length ? (
              <Typography color="text.secondary" variant="body2" sx={{ px: 1, pb: 1 }}>
                No scheduled deliveries.
              </Typography>
            ) : (
              <List dense>
                {schedules.map((schedule) => {
                  const view = savedViews.find((row) => row.id === schedule.savedReportId);
                  return (
                    <ListItem
                      key={schedule.id}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          aria-label="Cancel schedule"
                          onClick={async () => {
                            await deleteReportSchedule(schedule.id);
                            await reloadSavedData();
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      }
                    >
                      <ListItemText
                        primary={view?.name || "Saved view"}
                        secondary={`${schedule.cadence} delivery`}
                      />
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Paper>

          <Stack spacing={3}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Activity chart
              </Typography>
              <ActivityReportChart
                labels={chartData.labels}
                datasets={chartData.datasets}
                loading={loading}
                error={loadError ? errorMessage : ""}
              />
            </Paper>

            <Paper>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Period</TableCell>
                    {dimension === "tci" ? <TableCell>TCI</TableCell> : null}
                    {metricKeys.map((key) => (
                      <TableCell key={key}>{METRIC_LABELS[key]}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!tableRows.length ? (
                    <TableRow>
                      <TableCell colSpan={metricKeys.length + (dimension === "tci" ? 2 : 1)}>
                        <Typography color="text.secondary" variant="body2">
                          No metrics recorded for the selected period.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    tableRows.map((row) => (
                      <TableRow key={`${row.periodKey}-${row.tciId || "institution"}`}>
                        <TableCell>{row.periodKey}</TableCell>
                        {dimension === "tci" ? (
                          <TableCell>{row.tciName || "—"}</TableCell>
                        ) : null}
                        {metricKeys.map((key) => (
                          <TableCell key={key}>{row.metrics?.[key] || 0}</TableCell>
                        ))}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Paper>
          </Stack>
        </Box>

        <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle>Save this view</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              fullWidth
              label="View name"
              margin="dense"
              value={saveViewName}
              onChange={(event) => setSaveViewName(event.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <LoadingButton onClick={() => setSaveDialogOpen(false)}>Cancel</LoadingButton>
            <LoadingButton
              loading={savingView}
              onClick={handleSaveView}
              disabled={!saveViewName.trim()}
            >
              Save
            </LoadingButton>
          </DialogActions>
        </Dialog>

        <Dialog open={shareDialogOpen} onClose={() => setShareDialogOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle>Share view</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              fullWidth
              label="Account ID"
              margin="dense"
              value={shareTargetUid}
              onChange={(event) => setShareTargetUid(event.target.value)}
              helperText="Share within your institution only."
            />
          </DialogContent>
          <DialogActions>
            <LoadingButton onClick={() => setShareDialogOpen(false)}>Cancel</LoadingButton>
            <LoadingButton loading={sharingView} onClick={handleShareView}>
              Share
            </LoadingButton>
          </DialogActions>
        </Dialog>

        <Dialog
          open={scheduleDialogOpen}
          onClose={() => setScheduleDialogOpen(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Schedule this view</DialogTitle>
          <DialogContent>
            <TextField
              select
              fullWidth
              label="Cadence"
              margin="dense"
              value={scheduleCadence}
              onChange={(event) => setScheduleCadence(event.target.value)}
              SelectProps={{ native: true }}
            >
              {CADENCE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </TextField>
            <Alert severity="info" sx={{ mt: 2 }}>
              Reports are emailed only to your verified account address
              {user?.email ? ` (${user.email})` : ""}.
            </Alert>
          </DialogContent>
          <DialogActions>
            <LoadingButton onClick={() => setScheduleDialogOpen(false)}>Cancel</LoadingButton>
            <LoadingButton loading={schedulingView} onClick={handleScheduleView}>
              Create schedule
            </LoadingButton>
          </DialogActions>
        </Dialog>
      </Stack>
    </LocalizationProvider>
  );
};

function InstitutionReportsPage() {
  return (
    <RequireLauncherApp appId="fusionlead">
      <InstitutionReportsContent />
    </RequireLauncherApp>
  );
}

export default InstitutionReportsPage;
